/**
 * The goal writes more than one surface makes: give the goal more time, and mark it achieved.
 *
 * Promoted out of `pages/client-detail/lib/useGoalActs.ts` on 24 September 2026, when the task
 * card for `goalRenewalDue` became their second caller — "+2 tygodnie", "+4 tygodnie" and „Ukończ
 * cel" answer the task where it stands, and a page may not import a page.
 *
 * Both return the goal in its new state; the goal read is invalidated by PREFIX (it is cached per
 * content language, and a completed goal has to stop being active in both), and so is the queue,
 * because a deadline moved past the detector's seven days or a goal completed is a task that has
 * stopped being true (attention-queue R5).
 *
 * A promise per write, rejecting on a refusal — the caller decides how to say it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { attentionQueueQueryKey, clientGoalQueryKey } from '~/entities/client';
import {
  patchCustomerIdGoalGoalId,
  postCustomerIdGoalGoalIdComplete,
} from '~/shared/api/generated/endpoints/customer/customer';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';
import { toIsoDate } from '~/shared/lib/calendarDate';

import { extendedGoalDeadline } from '../model/goalExtension';

export interface GoalTarget {
  readonly clientId: number;
  readonly goalId: number;
}

export interface GoalWrites {
  /**
   * Give the goal `weeks` more, counted from the LATER of its current `deadline` and today —
   * `model/goalExtension` says why. Rejects without a request where either day is unreadable.
   */
  readonly extendGoal: (target: GoalTarget, deadline: string, weeks: number) => Promise<void>;
  readonly completeGoal: (target: GoalTarget) => Promise<void>;
}

export function useGoalWrites(): GoalWrites {
  const contentLang = useContentLanguage();
  const queryClient = useQueryClient();

  const settle = ({ clientId }: GoalTarget) => {
    void queryClient.invalidateQueries({ queryKey: clientGoalQueryKey(clientId) });
    void queryClient.invalidateQueries({ queryKey: attentionQueueQueryKey });
  };

  const extend = useMutation({
    mutationFn: ({ target, deadline }: { readonly target: GoalTarget; readonly deadline: string }) =>
      patchCustomerIdGoalGoalId(target.clientId, target.goalId, { deadline }, { lang: contentLang }),
    onSuccess: (_answer, { target }) => {
      settle(target);
    },
  });
  const complete = useMutation({
    mutationFn: (target: GoalTarget) =>
      postCustomerIdGoalGoalIdComplete(target.clientId, target.goalId, { lang: contentLang }),
    onSuccess: (_answer, target) => {
      settle(target);
    },
  });

  return {
    extendGoal: async (target, deadline, weeks) => {
      // Today is read at the PRESS, not at render: a tab left open overnight would otherwise count
      // an overdue goal's term from yesterday.
      const next = extendedGoalDeadline(deadline, toIsoDate(new Date()), weeks);
      if (next === null) throw new Error('The goal has no readable deadline to extend.');
      await extend.mutateAsync({ target, deadline: next });
    },
    completeGoal: async (target) => {
      await complete.mutateAsync(target);
    },
  };
}
